import React from 'react'
import PropTypes from 'prop-types'
import Information from '../Information'

/**
 * MultiStepFlowLayout
 *
 * Provides the overall structure for multi-step flows with fixed header/footer
 * and scrollable content area.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.header - Header component (typically MultiStepFlowHeader)
 * @param {React.ReactNode} props.children - Main content area
 * @param {React.ReactNode} props.footer - Footer component (typically MultiStepFlowFooter)
 * @param {string} props.className - Additional CSS classes for the wrapper
 * @param {string|Array|Object} props.errors - Error message(s) to display. Can be a string, array of strings, or array of objects with {title, description}
 */
const MultiStepFlowLayout = ({ header, children, footer, className = '', errors }) => {
  // Normalize errors to array format
  const normalizedErrors = React.useMemo(() => {
    if (!errors) return []

    // Single string
    if (typeof errors === 'string') {
      return [{ description: errors }]
    }

    // Array
    if (Array.isArray(errors)) {
      return errors.map(error => {
        // Already an object with title/description
        if (typeof error === 'object' && error !== null) {
          return error
        }
        // String in array
        return { description: String(error) }
      })
    }

    // Single object
    if (typeof errors === 'object') {
      return [errors]
    }

    return []
  }, [errors])

  return (
    <div className={`min-h-screen flex flex-col bg-background ${className}`}>
      {/* Fixed Header */}
      {header && (
        <div className="sticky top-0 z-10">
          {header}
        </div>
      )}

      {/* Scrollable Content Area */}
      <main className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-4 py-8">
          {/* Display Errors */}
          {normalizedErrors.length > 0 && (
            <div className="mb-6 space-y-4">
              {normalizedErrors.map((error, index) => (
                <Information
                  key={index}
                  variant="error"
                  title={error.title}
                  description={error.description}
                />
              ))}
            </div>
          )}

          {children}
        </div>
      </main>

      {/* Fixed Footer */}
      {footer && (
        <div className="">
          {footer}
        </div>
      )}
    </div>
  )
}

MultiStepFlowLayout.propTypes = {
  header: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  className: PropTypes.string,
  errors: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
      })
    ])),
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ])
}

export default MultiStepFlowLayout